import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight analytics: dual-writes to Plausible (if loaded) and to our
 * own analytics_events table (durable, queryable from the Admin console).
 *
 * Never throws — analytics failures must not break product flows.
 */

const SESSION_KEY = "claira:analytics:sid";

function sessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
}

export type AnalyticsEvent =
  | "signup_completed"
  | "onboarding_completed"
  | "screening_started"
  | "screening_completed"
  | "chat_message_sent"
  | "subscribe_clicked"
  | "subscription_active"
  | "journal_entry_created"
  | "crisis_resource_opened"
  | "pdf_downloaded"
  | "email_capture_prompted"
  | "journal_ai_generated"
  | "data_exported"
  | "account_deleted"
  | "trial_started"
  | "support_form_submitted";

type Props = Record<string, string | number | boolean | null | undefined>;

export function track(event: AnalyticsEvent, props: Props = {}) {
  if (typeof window === "undefined") return;

  // 1) Plausible (best-effort)
  try {
    const w = window as unknown as { plausible?: (e: string, opts?: { props?: Props }) => void };
    w.plausible?.(event, { props });
  } catch {
    /* noop */
  }

  // 2) Our own table — fire and forget
  void (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("analytics_events").insert({
        user_id: user?.id ?? null,
        session_id: sessionId(),
        event,
        path: window.location.pathname,
        props: props as never,
      });
    } catch {
      /* analytics must never break the app */
    }
  })();
}